/**
 * Purity Prayag Quiz Web Component
 * Implements personalized bundle recommendations based on user answers
 * Decision tree logic from specifications
 */

class Quiz extends HTMLElement {
  constructor() {
    super();
    this.currentQuestionIndex = 0;
    this.answers = {};
    this.partnerMapping = null;
    this.questionFlow = [];
    this.isInitialRender = true;
    
    // Bundle definitions based on CSV specifications
    this.bundleDefinitions = {
      1: {
        id: 1,
        name: 'Everyday Family Essentials',
        price: '₹1,500 – ₹2,200',
        idealFor: 'Family',
        description: 'Complete everyday essentials for the whole family',
        whyShowing: 'Perfect for daily family meals with balanced nutrition',
        skus: [
          { name: 'Unpolished Rice', quantity: '5 kg' },
          { name: 'Toor Dal', quantity: '2 kg' },
          { name: 'Cold-pressed Groundnut Oil', quantity: '2 L' },
          { name: 'Turmeric Powder', quantity: '200 g' },
          { name: 'Raw Honey', quantity: '500 g' }
        ]
      },
      2: {
        id: 2,
        name: 'Personal Clean Eating Starter',
        price: '₹1,000 – ₹1,500',
        idealFor: 'Individual',
        description: 'Perfect starter kit for clean eating journey',
        whyShowing: 'Ideal for individuals focused on personal nutrition',
        skus: [
          { name: 'Unpolished Rice', quantity: '2 kg' },
          { name: 'Moong Dal', quantity: '1 kg' },
          { name: 'Cold-pressed Oil', quantity: '1 L' },
          { name: 'Mixed Nuts', quantity: '250 g' }
        ]
      },
      3: {
        id: 3,
        name: 'Senior Wellness Pack',
        price: '₹1,500 – ₹2,500',
        idealFor: 'Seniors',
        description: 'Gentle on digestion, rich in nutrition',
        whyShowing: 'Curated for easy digestion and senior wellness needs',
        skus: [
          { name: 'Soft Rice / Little Millet', quantity: '3 kg' },
          { name: 'Moong Dal', quantity: '2 kg' },
          { name: 'Sesame Oil', quantity: '1 L' },
          { name: 'Raw Honey', quantity: '500 g' },
          { name: 'Turmeric Powder', quantity: '200 g' }
        ]
      },
      4: {
        id: 4,
        name: 'Kids Growth & Strength Pack',
        price: '₹1,500 – ₹2,300',
        idealFor: 'Kids',
        description: 'Nutrition-packed for growing children',
        whyShowing: 'Supports healthy growth with kid-friendly nutritious foods',
        skus: [
          { name: 'Rice or Atta', quantity: '3 kg' },
          { name: 'Toor Dal', quantity: '1.5 kg' },
          { name: 'Ghee or Oil', quantity: '1 L' },
          { name: 'Peanut Snack / Chikki', quantity: '400 g' },
          { name: 'Turmeric', quantity: '100 g' }
        ]
      },
      5: {
        id: 5,
        name: 'Energy & Immunity Boost Pack',
        price: '₹1,400 – ₹2,200',
        idealFor: 'Immunity',
        description: 'Boost your energy and strengthen immunity',
        whyShowing: 'Packed with immunity-boosting ingredients',
        skus: [
          { name: 'Foxtail / Kodo Millet', quantity: '2 kg' },
          { name: 'Raw Honey', quantity: '500 g' },
          { name: 'Mixed Nuts', quantity: '250 g' },
          { name: 'Turmeric Powder', quantity: '200 g' },
          { name: 'Cold-pressed Oil', quantity: '1 L' }
        ]
      },
      6: {
        id: 6,
        name: 'Weight & Metabolism Balance',
        price: '₹1,300 – ₹2,000',
        idealFor: 'Weight',
        description: 'Support healthy weight management naturally',
        whyShowing: 'Light yet nutritious options for metabolism support',
        skus: [
          { name: 'Barnyard / Little Millet', quantity: '2 kg' },
          { name: 'Cold-pressed Light Oil', quantity: '1 L' },
          { name: 'Flax / Seed Mix', quantity: '200 g' },
          { name: 'Cinnamon / Functional Spice', quantity: '100 g' }
        ]
      },
      7: {
        id: 7,
        name: 'Quick Meals Convenience Pack',
        price: '₹1,200 – ₹1,800',
        idealFor: 'Quick meals',
        description: 'Fast, healthy meals without compromise',
        whyShowing: 'Perfect for busy schedules with quick-cook options',
        skus: [
          { name: 'Ready Dal / Quick-cook Millet', quantity: '2 kg' },
          { name: 'Spice Blends', quantity: '3 units' },
          { name: 'Cold-pressed Oil', quantity: '1 L' },
          { name: 'Healthy Snack', quantity: '300 g' }
        ]
      },
      8: {
        id: 8,
        name: 'Guest & Hosting Pack',
        price: '₹1,800 – ₹3,000+',
        idealFor: 'Hosting',
        description: 'Premium selection for special occasions',
        whyShowing: 'Impress your guests with premium quality ingredients',
        skus: [
          { name: 'Premium Rice', quantity: '3 kg' },
          { name: 'Mixed Dry Fruits', quantity: '500 g' },
          { name: 'Spices Gift Set', quantity: '3 units' },
          { name: 'Raw Honey', quantity: '500 g' }
        ]
      },
      9: {
        id: 9,
        name: 'Purity Prayag Brand-Only Pack',
        price: 'Varies',
        idealFor: 'Purity Prayag lovers',
        description: '100% Purity Prayag products only',
        whyShowing: 'Exclusively curated with Purity Prayag products',
        skus: [] // Dynamic based on mapped bundle
      }
    };

    // Decision tree mappings from CSV
    this.decisionTree = {
      // Q1: Who are you shopping for? → Allowed Bundles
      q1AllowedBundles: {
        'myself': [1, 2, 5, 6, 7],
        'my_family': [1, 5, 7],
        'my_parents': [3, 5],
        'my_children': [4, 1],
        'guests_events': [8]
      },
      // Q2: Cooking Style → Priority Weight
      q2PriorityBundles: {
        'daily_home_cooking': [1, 3, 4],
        'light_quick_meals': [7],
        'health_focused': [5, 6],
        'occasional_weekend': [8]
      },
      // Q3: Health Focus → Forced Inclusions
      q3ForcedInclusions: {
        'everyday_nutrition': ['Rice', 'Dal', 'Oil'],
        'energy_immunity': ['Honey', 'Turmeric', 'Nuts'],
        'weight_metabolism': ['Millets', 'Light Oil'],
        'kids_growth': ['Ghee/Oil', 'Peanut snack'],
        'senior_wellness': ['Light oil', 'Soft grains']
      },
      // Q5: Budget → Bundle Size
      q5BundleSize: {
        'budget_1000_1500': { min: 4, max: 5, label: '₹1,000–₹1,500' },
        'budget_1500_3000': { min: 6, max: 8, label: '₹1,500–₹3,000' },
        'budget_3000_plus': { min: 8, max: 10, label: '₹3,000+', premium: true }
      }
    };
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Parse quiz data from JSON script
    const dataScript = this.querySelector('[data-quiz-data]');
    if (dataScript) {
      try {
        this.quizData = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('Failed to parse quiz data:', e);
        return;
      }
    }

    // Check if form was successfully submitted and show results
    this.checkAndShowResultsAfterSubmission();

    // Initialize answers object
    this.initializeAnswers();

    // Cache DOM elements
    this.questionContainer = this.querySelector('.quiz-question-container');
    this.prevBtn = this.querySelector('.quiz-prev-btn');
    this.nextBtn = this.querySelector('.quiz-next-btn');
    this.progressIndicator = this.querySelector('.quiz-progress-indicator');

    // Store the original button HTML content (including icon)
    if (this.nextBtn && !this.nextBtn.hasAttribute('data-default-html')) {
      this.nextBtn.setAttribute('data-default-html', this.nextBtn.innerHTML);
    }

    // Bind event listeners
    this.prevBtn?.addEventListener('click', () => this.goToPrevious());
    this.nextBtn?.addEventListener('click', () => this.goToNext());

    // Initialize question flow
    this.questionFlow = [];
    if (this.quizData && this.quizData.questions) {
      this.quizData.questions.forEach((q, index) => {
        this.questionFlow.push(q.id || `q${index + 1}`);
      });
    }
    this.currentQuestionIndex = 0;

    // Render initial question
    this.renderQuestion();
    this.updateNavigation();
  }

  checkAndShowResultsAfterSubmission() {
    // Check if form was successfully submitted
    const successMessage = this.querySelector('.quiz-form-success');
    if (successMessage) {
      try {
        const storedData = sessionStorage.getItem('quiz-data');
        if (storedData) {
          const quizData = JSON.parse(storedData);
          
          // Restore answers for results display
          this.answers = quizData.answers;
          
          // Hide quiz container
          const quizContainer = this.querySelector('.quiz-container');
          if (quizContainer) {
            quizContainer.style.display = 'none';
          }
          
          // Create and show results container
          const resultsContainer = document.createElement('div');
          resultsContainer.className = 'quiz-results-container';
          resultsContainer.innerHTML = this.buildResultsHTML();
          
          // Insert results before the customer form
          const customerForm = this.querySelector('.quiz-customer-form');
          if (customerForm && customerForm.parentNode) {
            customerForm.parentNode.insertBefore(resultsContainer, customerForm);
          } else {
            this.appendChild(resultsContainer);
          }
          
          // Scroll to results
          setTimeout(() => {
            resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
          
          // Clear stored data
          sessionStorage.removeItem('quiz-data');
        }
      } catch (e) {
        console.error('Failed to retrieve quiz results:', e);
      }
    }
  }

  initializeAnswers() {
    // Initialize answers for the 5 questions
    this.answers = {
      q1: null, // Who are you shopping for?
      q2: null, // Cooking style
      q3: null, // Primary health focus
      q4: null, // Brand preference
      q5: null  // Budget comfort
    };
  }

  getCurrentQuestionId() {
    return this.questionFlow[this.currentQuestionIndex];
  }

  renderQuestion() {
    const questionId = this.getCurrentQuestionId();
    if (!questionId) return;

    const questionData = this.quizData.questions.find(q => q.id === questionId);
    if (!questionData) return;

    // Update image display
    this.updateQuestionImage(questionData);

    // Clear previous question
    if (this.questionContainer) {
      this.questionContainer.innerHTML = '';
    }

    // Build question HTML
    const questionHTML = this.buildQuestionHTML(questionId, questionData);
    if (this.questionContainer) {
      this.questionContainer.innerHTML = questionHTML;
    }

    // Attach event listeners
    this.attachQuestionListeners(questionId, questionData);

    // Update progress
    this.updateProgress();

    // Scroll to top (but not on initial page load)
    if (!this.isInitialRender) {
      this.scrollToTop();
    } else {
      this.isInitialRender = false;
    }
  }

  updateQuestionImage(questionData) {
    const imageWrappers = this.querySelectorAll('.quiz-image-wrapper');
    
    if (imageWrappers.length === 0) return;

    // Remove active class from all images
    imageWrappers.forEach(wrapper => {
      wrapper.classList.remove('active');
    });

    // Add active class to current question's image
    const imageIndex = questionData.imageIndex !== undefined ? questionData.imageIndex : this.currentQuestionIndex;
    const currentImageWrapper = this.querySelector(`[data-question-index="${imageIndex}"]`);
    
    if (currentImageWrapper) {
      currentImageWrapper.classList.add('active');
      
      if (questionData.imageBackgroundColor) {
        currentImageWrapper.style.backgroundColor = questionData.imageBackgroundColor;
      }
    }
  }

  buildQuestionHTML(questionId, questionData) {
    let html = '';

    // Question header
    html += `
      <div class="quiz-question-header">
        <div class="quiz-question-number">${questionData.number}</div>
        <div class="quiz-question-title">${questionData.title}</div>
      </div>
    `;

    // Question text
    html += `<div class="quiz-question-text">${questionData.text}</div>`;

    // Options
    html += this.buildOptionsHTML(questionId, questionData);

    // "Why I ask" section
    if (questionData.whyIAsk) {
      html += `
        <div class="quiz-why-i-ask">
          <strong>${this.quizData.whyIAskLabel || 'Why we ask:'}</strong> ${questionData.whyIAsk}
        </div>
      `;
    }

    return html;
  }

  buildOptionsHTML(questionId, questionData) {
    let html = '<div class="quiz-options-list">';
    html += '<div class="quiz-partner-group">';
    html += '<div class="quiz-partner-options">';
    
    const selectType = questionData.selectType || 'single';
    
    questionData.options.forEach((option, index) => {
      const currentAnswer = this.answers[questionId];
      let isSelected = false;
      
      if (selectType === 'multi') {
        isSelected = Array.isArray(currentAnswer) && currentAnswer.includes(option.value);
      } else {
        isSelected = currentAnswer === option.value;
      }
      
      html += `
        <div class="quiz-option ${isSelected ? 'selected' : ''}" 
             data-option-value="${option.value}"
             data-select-type="${selectType}">
          <div class="quiz-option-text">${option.label}</div>
        </div>
      `;
    });
    
    html += '</div></div></div>';
    return html;
  }

  attachQuestionListeners(questionId, questionData) {
    const options = this.questionContainer.querySelectorAll('.quiz-option[data-option-value]');
    
    options.forEach(option => {
      option.addEventListener('click', () => {
        this.handleOptionClick(questionId, option, questionData);
      });
    });
  }

  handleOptionClick(questionId, optionElement, questionData) {
    const optionValue = optionElement.dataset.optionValue;
    const selectType = optionElement.dataset.selectType || 'single';
    const isMultiSelect = selectType === 'multi';

    if (isMultiSelect) {
      // Multi-select handling
      if (!Array.isArray(this.answers[questionId])) {
        this.answers[questionId] = [];
      }
      
      const index = this.answers[questionId].indexOf(optionValue);
      
      if (index > -1) {
        this.answers[questionId].splice(index, 1);
        optionElement.classList.remove('selected');
      } else {
        this.answers[questionId].push(optionValue);
        optionElement.classList.add('selected');
      }
    } else {
      // Single select handling
      this.questionContainer.querySelectorAll('.quiz-option').forEach(opt => {
        opt.classList.remove('selected');
      });
      
      optionElement.classList.add('selected');
      this.answers[questionId] = optionValue;
      
      // Store partner mapping from Q1
      if (questionId === 'q1') {
        this.partnerMapping = optionValue;
      }
      
      // Auto-advance for single select
      setTimeout(() => {
        this.goToNext();
      }, 300);
    }

    this.updateNavigation();
  }

  updateProgress() {
    const totalQuestions = this.questionFlow.length;
    const currentQuestion = this.currentQuestionIndex + 1;
    
    if (this.progressIndicator) {
      this.progressIndicator.textContent = `${currentQuestion}/${totalQuestions}`;
    }
  }

  updateNavigation() {
    // Previous button
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentQuestionIndex === 0;
    }

    // Next button
    if (this.nextBtn) {
      const questionId = this.getCurrentQuestionId();
      const isLastQuestion = this.currentQuestionIndex === this.questionFlow.length - 1;

      if (isLastQuestion) {
        this.nextBtn.innerHTML = this.quizData.finishText || 'Finish';
        this.nextBtn.classList.add('finish');
      } else {
        const defaultButtonHTML = this.nextBtn.getAttribute('data-default-html');
        if (defaultButtonHTML) {
          this.nextBtn.innerHTML = defaultButtonHTML;
        }
        this.nextBtn.classList.remove('finish');
      }

      const isAnswered = this.isQuestionAnswered(questionId);
      this.nextBtn.disabled = !isAnswered;
    }
  }

  isQuestionAnswered(questionId) {
    const answer = this.answers[questionId];
    
    if (answer === null || answer === undefined) {
      return false;
    }
    
    if (Array.isArray(answer)) {
      return answer.length > 0;
    }
    
    return true;
  }

  goToPrevious() {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
      this.renderQuestion();
      this.updateNavigation();
    }
  }

  goToNext() {
    const questionId = this.getCurrentQuestionId();
    const isLastQuestion = this.currentQuestionIndex === this.questionFlow.length - 1;

    if (!this.isQuestionAnswered(questionId)) {
      return;
    }

    if (isLastQuestion) {
      this.finishQuiz();
    } else {
      this.currentQuestionIndex++;
      this.renderQuestion();
      this.updateNavigation();
    }
  }

  finishQuiz() {
    if (this.quizData.resultsUrl && !this.quizData.showResultsInline) {
      this.redirectToResults();
    } else {
      this.showResults();
    }
  }

  redirectToResults() {
    const url = new URL(this.quizData.resultsUrl, window.location.origin);
    
    Object.keys(this.answers).forEach(questionId => {
      const answer = this.answers[questionId];
      if (answer !== null && answer !== undefined) {
        if (Array.isArray(answer)) {
          url.searchParams.append(questionId, answer.join(','));
        } else {
          url.searchParams.append(questionId, answer);
        }
      }
    });
    
    window.location.href = url.toString();
  }

  showResults() {
    // Hide quiz container
    const quizContainer = this.querySelector('.quiz-container');
    if (quizContainer) {
      quizContainer.style.display = 'none';
    }

    // Store quiz data in sessionStorage
    try {
      sessionStorage.setItem('quiz-data', JSON.stringify({
        answers: this.answers,
        resultsHTML: this.buildResultsHTML()
      }));
    } catch (e) {
      console.error('Failed to store quiz data:', e);
    }

    this.showCustomerForm();
  }

  showCustomerForm() {
    const customerForm = this.querySelector('.quiz-customer-form');
    if (!customerForm) {
      console.error('Customer form not found');
      return;
    }

    this.updateQuizDataInNote();
    this.updateQuizTags();

    // Show the form
    customerForm.style.display = 'block';
    customerForm.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Handle form submission
    const form = customerForm.querySelector('form');
    if (form) {
      if (!form.action || form.action === '' || form.action === window.location.href) {
        form.action = '/contact';
      }
      form.method = 'post';
      
      this.formSubmitHandler = (e) => {
        this.updateQuizDataInNote();
        this.updateQuizTags();
        
        // Update full phone number
        const phoneInput = this.querySelector('#QuizForm-phone');
        const phoneCountrySelect = this.querySelector('#QuizForm-phone-country');
        const phoneFullField = this.querySelector('#QuizForm-phone-full');
        if (phoneInput && phoneCountrySelect) {
          const phoneNumber = phoneInput.value.trim();
          const countryCode = phoneCountrySelect.value;
          if (phoneNumber) {
            const fullPhone = `${countryCode}${phoneNumber}`;
            if (phoneFullField) {
              phoneFullField.value = fullPhone;
            }
            phoneInput.value = fullPhone;
          }
        }
        
        // Validate quiz data
        const quizAnswersField = this.querySelector('#QuizForm-quiz-answers');
        if (!quizAnswersField || !quizAnswersField.value) {
          const errorMsg = this.quizData.errorMessages?.quizDataMissing || 'Error: Quiz data is missing.';
          alert(errorMsg);
          e.preventDefault();
          return false;
        }
        
        // Validate required fields
        const firstNameInput = this.querySelector('#QuizForm-FirstName');
        const lastNameInput = this.querySelector('#QuizForm-LastName');
        const emailInput = this.querySelector('#QuizForm-email');
        const errorMessages = this.quizData.errorMessages || {};
        
        if (!firstNameInput?.value.trim()) {
          alert(errorMessages.firstNameRequired || 'Please enter your first name');
          e.preventDefault();
          return false;
        }
        
        if (!lastNameInput?.value.trim()) {
          alert(errorMessages.lastNameRequired || 'Please enter your last name');
          e.preventDefault();
          return false;
        }
        
        if (!emailInput?.value.trim()) {
          alert(errorMessages.emailRequired || 'Please enter your email address');
          e.preventDefault();
          return false;
        }
        
        if (!phoneInput?.value.trim()) {
          alert(errorMessages.phoneRequired || 'Please enter your phone number');
          e.preventDefault();
          return false;
        }
        
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = this.quizData.submittingText || 'Getting your recommendations...';
        }
        
        return true;
      };
      
      form.addEventListener('submit', this.formSubmitHandler);
    }
  }

  updateQuizDataInNote() {
    const quizAnswersField = this.querySelector('#QuizForm-quiz-answers');
    if (!quizAnswersField) return;

    const firstNameInput = this.querySelector('#QuizForm-FirstName');
    const lastNameInput = this.querySelector('#QuizForm-LastName');
    const phoneInput = this.querySelector('#QuizForm-phone');
    const phoneCountrySelect = this.querySelector('#QuizForm-phone-country');
    const emailInput = this.querySelector('#QuizForm-email');
    
    const recommendedBundles = this.calculateRecommendedBundles();
    
    const quizData = {
      answers: this.answers,
      recommendedBundles: recommendedBundles.map(b => ({
        id: b.id,
        name: b.name,
        price: b.price
      })),
      customerInfo: {
        firstName: firstNameInput?.value.trim() || '',
        lastName: lastNameInput?.value.trim() || '',
        email: emailInput?.value.trim() || '',
        phone: phoneInput?.value.trim() || ''
      },
      completedAt: new Date().toISOString()
    };
    
    quizAnswersField.value = JSON.stringify(quizData, null, 2);
  }

  updateQuizTags() {
    const tagsField = this.querySelector('#QuizForm-quiz-tags');
    const customerTagsField = this.querySelector('#QuizForm-quiz-tags-customer');
    
    const tags = ['quiz-submission'];
    
    // Add all answer values as tags
    Object.keys(this.answers).forEach(questionId => {
      const answer = this.answers[questionId];
      if (answer) {
        if (Array.isArray(answer)) {
          answer.forEach(v => tags.push(`${questionId}:${v}`));
        } else {
          tags.push(`${questionId}:${answer}`);
        }
      }
    });
    
    // Add recommended bundles
    const recommendedBundles = this.calculateRecommendedBundles();
    recommendedBundles.forEach(b => {
      tags.push(`recommended-bundle:${b.id}`);
    });
    
    const tagsString = tags.join(', ');
    
    if (tagsField) tagsField.value = tagsString;
    if (customerTagsField) customerTagsField.value = tagsString;
  }

  /**
   * Calculate recommended bundles based on quiz answers using decision tree
   */
  calculateRecommendedBundles() {
    const q1 = this.answers.q1; // Who are you shopping for?
    const q2 = this.answers.q2; // Cooking style
    const q3 = this.answers.q3; // Health focus
    const q4 = this.answers.q4; // Brand preference
    const q5 = this.answers.q5; // Budget

    // Step 1: Get allowed bundles from Q1
    let allowedBundles = this.decisionTree.q1AllowedBundles[q1] || [1, 2, 3, 4, 5, 6, 7, 8];
    
    // Step 2: Get priority bundles from Q2
    let priorityBundles = this.decisionTree.q2PriorityBundles[q2] || [];
    
    // Step 3: Score bundles based on health focus
    const healthFocusBundleMapping = {
      'everyday_nutrition': [1, 2],      // Family Essentials, Personal Starter
      'energy_immunity': [5],            // Energy & Immunity Boost
      'weight_metabolism': [6],          // Weight & Metabolism Balance
      'kids_growth': [4],                // Kids Growth
      'senior_wellness': [3]             // Senior Wellness
    };
    
    let healthFocusBundles = healthFocusBundleMapping[q3] || [];
    
    // Step 4: Check brand preference
    const isPurityPrayagOnly = q4 === 'purity_prayag_only';
    
    // Step 5: Calculate final bundle scores
    let bundleScores = {};
    
    // Initialize scores for allowed bundles
    allowedBundles.forEach(bundleId => {
      bundleScores[bundleId] = 0;
      
      // Add priority score
      if (priorityBundles.includes(bundleId)) {
        bundleScores[bundleId] += 3;
      }
      
      // Add health focus score
      if (healthFocusBundles.includes(bundleId)) {
        bundleScores[bundleId] += 5;
      }
    });
    
    // Sort bundles by score
    let sortedBundles = Object.entries(bundleScores)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => parseInt(id));
    
    // Get top 2-3 bundles
    let recommendedBundleIds = sortedBundles.slice(0, 3);
    
    // Ensure we have at least one recommendation
    if (recommendedBundleIds.length === 0) {
      recommendedBundleIds = [1]; // Default to Everyday Family Essentials
    }
    
    // If Purity Prayag only, add bundle 9 as primary and modify others
    if (isPurityPrayagOnly) {
      // Create a brand-only version based on the top recommendation
      const primaryBundleId = recommendedBundleIds[0];
      const brandOnlyBundle = { ...this.bundleDefinitions[9] };
      brandOnlyBundle.name = `${this.bundleDefinitions[primaryBundleId]?.name || 'Custom'} — Purity Prayag Only`;
      brandOnlyBundle.skus = this.bundleDefinitions[primaryBundleId]?.skus || [];
      
      // Replace with brand-only version
      recommendedBundleIds = [9, ...recommendedBundleIds.filter(id => id !== 9).slice(0, 2)];
    }
    
    // Build final bundle objects with context
    const recommendations = recommendedBundleIds.map((bundleId, index) => {
      const bundle = { ...this.bundleDefinitions[bundleId] };
      
      // Add personalized "why showing" based on answers
      bundle.whyShowing = this.getPersonalizedWhyShowing(bundleId, q1, q2, q3);
      bundle.isPrimary = index === 0;
      
      // Adjust SKUs based on budget
      if (q5) {
        const budgetConfig = this.decisionTree.q5BundleSize[q5];
        if (budgetConfig) {
          bundle.budgetFit = budgetConfig.label;
          if (budgetConfig.premium && bundle.skus) {
            bundle.note = 'Premium upgrades available';
          }
        }
      }
      
      return bundle;
    });
    
    return recommendations;
  }

  getPersonalizedWhyShowing(bundleId, q1, q2, q3) {
    const shoppingForLabels = {
      'myself': 'you',
      'my_family': 'your family',
      'my_parents': 'your parents',
      'my_children': 'your children',
      'guests_events': 'your guests'
    };
    
    const cookingStyleLabels = {
      'daily_home_cooking': 'daily cooking routine',
      'light_quick_meals': 'quick meal preferences',
      'health_focused': 'health-focused lifestyle',
      'occasional_weekend': 'weekend cooking style'
    };
    
    const healthFocusLabels = {
      'everyday_nutrition': 'everyday nutrition',
      'energy_immunity': 'energy & immunity goals',
      'weight_metabolism': 'weight & metabolism focus',
      'kids_growth': 'growth & development needs',
      'senior_wellness': 'wellness requirements'
    };
    
    const target = shoppingForLabels[q1] || 'you';
    const cooking = cookingStyleLabels[q2] || '';
    const health = healthFocusLabels[q3] || '';
    
    return `Selected for ${target} based on your ${cooking} and ${health}.`;
  }

  buildResultsHTML() {
    const recommendedBundles = this.calculateRecommendedBundles();
    const shoppingForLabel = this.getShoppingForLabel(this.answers.q1);
    
    // Personalized header based on who they're shopping for (from CSV)
    const targetLabel = this.getTargetLabel(this.answers.q1);
    
    let html = `
      <div class="quiz-results-header">
        <h2>Your everyday food match for ${targetLabel}</h2>
        <p class="quiz-results-subtitle">
          Based on your cooking habits, health focus, and budget comfort — here's what fits your home best.
        </p>
      </div>
      
      <div class="quiz-results-summary">
        <div class="quiz-results-summary-item">
          <span class="quiz-summary-label">Shopping for:</span>
          <span class="quiz-summary-value">${shoppingForLabel}</span>
        </div>
        <div class="quiz-results-summary-item">
          <span class="quiz-summary-label">Cooking style:</span>
          <span class="quiz-summary-value">${this.getCookingStyleLabel(this.answers.q2)}</span>
        </div>
        <div class="quiz-results-summary-item">
          <span class="quiz-summary-label">Health focus:</span>
          <span class="quiz-summary-value">${this.getHealthFocusLabel(this.answers.q3)}</span>
        </div>
      </div>
      
      <div class="quiz-results-bundles">
        <h3>Recommended Bundles for You</h3>
        <div class="quiz-bundles-grid">
    `;
    
    recommendedBundles.forEach((bundle, index) => {
      // Calculate savings (approximate 10-15% savings vs individual purchase)
      const savingsPercent = bundle.isPrimary ? 15 : 10;
      const savingsText = `Save ${savingsPercent}% vs buying individually`;
      
      html += `
        <div class="quiz-bundle-card ${bundle.isPrimary ? 'primary' : ''}">
          ${bundle.isPrimary ? '<div class="quiz-bundle-badge">Best Match</div>' : ''}
          <div class="quiz-bundle-header">
            <h4 class="quiz-bundle-name">${bundle.name}</h4>
            <div class="quiz-bundle-pricing">
              <span class="quiz-bundle-price">${bundle.price}</span>
              <span class="quiz-bundle-savings">${savingsText}</span>
            </div>
          </div>
          <p class="quiz-bundle-ideal-for">Ideal for: ${bundle.idealFor}</p>
          <p class="quiz-bundle-why"><strong>Why you're seeing this:</strong> ${bundle.whyShowing}</p>
          
          <div class="quiz-bundle-contents expandable" data-expanded="false">
            <button class="quiz-bundle-contents-toggle" onclick="this.parentElement.dataset.expanded = this.parentElement.dataset.expanded === 'true' ? 'false' : 'true'">
              <span class="toggle-text">What's inside</span>
              <span class="quiz-toggle-icon">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
            <ul class="quiz-bundle-sku-list">
              ${bundle.skus.map(sku => `<li>${sku.name} — ${sku.quantity}</li>`).join('')}
            </ul>
          </div>
          
          ${bundle.budgetFit ? `<p class="quiz-bundle-budget-fit"><strong>Budget fit:</strong> ${bundle.budgetFit}</p>` : ''}
          ${bundle.note ? `<p class="quiz-bundle-note">${bundle.note}</p>` : ''}
          
          <button class="quiz-bundle-cta primary-cta" onclick="window.addBundleToCart && window.addBundleToCart('${bundle.id}')">
            Add Full Bundle to Cart
          </button>
        </div>
      `;
    });
    
    html += `
        </div>
      </div>
      
      <!-- Optional Swaps Section (from CSV) -->
      <div class="quiz-results-swaps">
        <h4>Optional Customizations</h4>
        <p class="quiz-swaps-subtitle">Personalize your bundle with these optional swaps:</p>
        <div class="quiz-swaps-options">
          <button class="quiz-swap-option" data-swap="oil">
            <span class="quiz-swap-icon">🫒</span>
            <span class="quiz-swap-text">Swap oil type</span>
          </button>
          <button class="quiz-swap-option" data-swap="dal">
            <span class="quiz-swap-icon">🫘</span>
            <span class="quiz-swap-text">Swap dal variety</span>
          </button>
          <button class="quiz-swap-option" data-swap="snack">
            <span class="quiz-swap-icon">🥜</span>
            <span class="quiz-swap-text">Add kids snack pack</span>
          </button>
          <button class="quiz-swap-option" data-swap="millet">
            <span class="quiz-swap-icon">🌾</span>
            <span class="quiz-swap-text">Upgrade to millet</span>
          </button>
        </div>
      </div>
      
      <!-- Cross-link CTAs (from CSV) -->
      <div class="quiz-results-actions">
        <a href="/collections/all" class="quiz-action-link primary">
          <span class="quiz-action-icon">🛒</span>
          View Individual Products
        </a>
        <button onclick="location.reload()" class="quiz-action-link secondary">
          <span class="quiz-action-icon">↻</span>
          Refine Answers
        </button>
        <a href="/pages/purity-hub" class="quiz-action-link secondary">
          <span class="quiz-action-icon">📚</span>
          Learn Why These Foods Were Chosen
        </a>
        <a href="/collections/purity-prayag" class="quiz-action-link secondary">
          <span class="quiz-action-icon">✨</span>
          Shop Only Purity Prayag Versions
        </a>
      </div>
    `;
    
    return html;
  }

  getTargetLabel(q1Value) {
    const targetLabels = {
      'myself': 'You',
      'my_family': 'Your Family',
      'my_parents': 'Your Parents',
      'my_children': 'Your Kids',
      'guests_events': 'Your Guests'
    };
    return targetLabels[q1Value] || 'You';
  }

  getShoppingForLabel(value) {
    const labels = {
      'myself': 'Myself',
      'my_family': 'My Family',
      'my_parents': 'My Parents',
      'my_children': 'My Children',
      'guests_events': 'Guests/Events'
    };
    return labels[value] || value;
  }

  getCookingStyleLabel(value) {
    const labels = {
      'daily_home_cooking': 'Daily home cooking',
      'light_quick_meals': 'Light & quick meals',
      'health_focused': 'Health-focused cooking',
      'occasional_weekend': 'Occasional / weekend cooking'
    };
    return labels[value] || value;
  }

  getHealthFocusLabel(value) {
    const labels = {
      'everyday_nutrition': 'Everyday nutrition',
      'energy_immunity': 'Energy & immunity',
      'weight_metabolism': 'Weight & metabolism',
      'kids_growth': "Kids' growth",
      'senior_wellness': 'Senior wellness'
    };
    return labels[value] || value;
  }

  scrollToTop() {
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

// Register the custom element
if (!customElements.get('grocery-quiz')) {
  customElements.define('grocery-quiz', Quiz);
}
